import type { Color } from './color.js';
import { getLocale } from './i18n.js';
import { addDays, dateKey, monthGrid, nextMonth, prevMonth, daysInMonth, weekday } from './dates.js';
import { expandEvents, resolveCellBackground, type Occurrence } from './events.js';
import {
  capHeightMm,
  layoutTextBlock,
  shapeRun,
  type FontProvider,
  type TextRun,
} from './fonts.js';
import { effectivePpi, ptToMm } from './units.js';
import { expandTokens, pageGeometry, pageInfo, pageMonths, photoPageKey, resolveDesign } from './project.js';
import type { Element, GridEl, HolidayListEl, ImageEl, KeyEl, LegendEl, LogoEl, MiniEl, PageKey, ProjectDoc, Rect, Stroke, TextEl, TextStyle, YearGridEl } from './schema.js';

export interface AssetMeta {
  id: string;
  width: number; // oriented pixel dimensions
  height: number;
  hasAlpha: boolean;
  mime: string;
}

export type Prim =
  | { k: 'rect'; x: number; y: number; w: number; h: number; r: number; fill: Color | null; opacity: number; stroke: Stroke | null; el?: string }
  | { k: 'line'; x1: number; y1: number; x2: number; y2: number; stroke: Stroke; el?: string }
  | { k: 'text'; fontId: string; sizePt: number; color: Color; letterSpacingPt: number; run: TextRun; el?: string }
  | {
      k: 'image';
      assetId: string;
      /** visible area (frame, possibly extended into bleed) */
      clip: Rect;
      r: number;
      /** full placed image rectangle, may extend beyond the clip */
      img: Rect;
      el?: string;
    }
  | { k: 'placeholder'; kind: 'photo' | 'logo'; x: number; y: number; w: number; h: number; r: number; label: string; /** what the frame needs, e.g. "120 × 80 mm · 1418 × 945 px at 300 ppi" */ hint?: string; el?: string };

export type Severity = 'error' | 'warning' | 'info';
export interface Issue {
  id: string;
  severity: Severity;
  code: string;
  message: string;
  pageKey?: PageKey;
  elementId?: string;
}

export interface ElementBox {
  id: string;
  type: Element['type'];
  name: string;
  rect: Rect;
}

export interface ImageReport {
  elementId: string;
  assetId: string;
  ppi: number;
  coversBleed: boolean;
  /** visible region of the source image in source pixels (for export cropping) */
  source: { x: number; y: number; w: number; h: number };
}

export interface PageLayout {
  key: PageKey;
  trimW: number;
  trimH: number;
  bleed: number;
  prims: Prim[];
  boxes: ElementBox[];
  issues: Issue[];
  images: ImageReport[];
}

export interface LayoutEnv {
  fonts: FontProvider;
  assets: Record<string, AssetMeta | undefined>;
  occurrences?: Map<string, Occurrence[]>;
}

export function prepareOccurrences(doc: ProjectDoc): Map<string, Occurrence[]> {
  return expandEvents(doc.eventSets, dateKey(doc.year - 1, 11, 1), dateKey(doc.year + 1, 2, 28));
}

const EDGE_TOL = 0.5;

class Ctx {
  prims: Prim[] = [];
  issues: Issue[] = [];
  boxes: ElementBox[] = [];
  images: ImageReport[] = [];
  constructor(public doc: ProjectDoc, public key: PageKey, public env: LayoutEnv, public occ: Map<string, Occurrence[]>) {}
  issue(severity: Severity, code: string, message: string, elementId?: string, suffix = '') {
    const id = `${this.key}:${elementId ?? '-'}:${code}${suffix ? ':' + suffix : ''}`;
    if (!this.issues.some((i) => i.id === id)) this.issues.push({ id, severity, code, message, pageKey: this.key, elementId });
  }
  font(style: TextStyle, elementId: string) {
    const f = this.env.fonts.get(style.fontId);
    if (!f) this.issue('error', 'font-missing', `Font "${style.fontId}" is not available. Upload it or choose another font — it will not be substituted.`, elementId, style.fontId);
    return f?.font;
  }
  text(style: TextStyle, text: string, box: Rect, valign: 'top' | 'middle' | 'bottom', elementId: string, opts: { wrap?: boolean; maxLines?: number; color?: Color } = {}) {
    const font = this.font(style, elementId);
    if (!font || !text) return { overflow: false, height: 0 };
    const block = layoutTextBlock(font, text, style, box, valign, opts);
    for (const run of block.runs)
      if (run.text) this.prims.push({ k: 'text', fontId: style.fontId, sizePt: style.sizePt, color: opts.color ?? style.color, letterSpacingPt: style.letterSpacingPt, run, el: elementId });
    return block;
  }
  /** single-line text placed with its baseline at y and x aligned at anchor */
  line(style: TextStyle, text: string, x: number, baseline: number, anchor: 'left' | 'center' | 'right', elementId: string, color?: Color) {
    const font = this.font(style, elementId);
    if (!font || !text) return 0;
    const t = style.uppercase ? text.toUpperCase() : text;
    const { glyphs, width } = shapeRun(font, t, style.sizePt, style.letterSpacingPt);
    const left = anchor === 'left' ? x : anchor === 'center' ? x - width / 2 : x - width;
    this.prims.push({ k: 'text', fontId: style.fontId, sizePt: style.sizePt, color: color ?? style.color, letterSpacingPt: style.letterSpacingPt, run: { text: t, x: left, y: baseline, width, glyphs }, el: elementId });
    return width;
  }
}

export function layoutPage(doc: ProjectDoc, key: PageKey, env: LayoutEnv): PageLayout {
  const occ = env.occurrences ?? prepareOccurrences(doc);
  const ctx = new Ctx(doc, key, env, occ);
  const geo = pageGeometry(doc, key);
  const design = resolveDesign(doc, key);
  if (design.background)
    ctx.prims.push({ k: 'rect', x: -geo.bleed, y: -geo.bleed, w: geo.trimW + 2 * geo.bleed, h: geo.trimH + 2 * geo.bleed, r: 0, fill: design.background, opacity: 1, stroke: null });
  for (const el of design.elements) {
    if (el.hidden) continue;
    ctx.boxes.push({ id: el.id, type: el.type, name: el.name, rect: el.rect });
    if (!(el.rect.w > 0 && el.rect.h > 0) || ![el.rect.x, el.rect.y, el.rect.w, el.rect.h].every(Number.isFinite)) {
      ctx.issue('error', 'bad-geometry', `"${el.name}" has an invalid size (width and height must be greater than 0 mm).`, el.id);
      continue;
    }
    if (el.rect.x >= geo.trimW || el.rect.y >= geo.trimH || el.rect.x + el.rect.w <= 0 || el.rect.y + el.rect.h <= 0) {
      ctx.issue('error', 'off-page', `"${el.name}" is completely outside the page.`, el.id);
      continue;
    }
    switch (el.type) {
      case 'text': layoutTextEl(ctx, el); break;
      case 'shape': {
        // filled panels that touch the trim edge run into the bleed (no white sliver after cutting)
        const r = el.fill && el.opacity >= 1 ? bleedFrame(el.rect, geo, el.radiusMm) : el.rect;
        if (el.fill) ctx.prims.push({ k: 'rect', ...r, r: el.radiusMm, fill: el.fill, opacity: el.opacity, stroke: null, el: el.id });
        if (el.stroke && el.stroke.widthPt > 0) ctx.prims.push({ k: 'rect', ...el.rect, r: el.radiusMm, fill: null, opacity: 1, stroke: el.stroke, el: el.id });
        break;
      }
      case 'image': layoutImage(ctx, el, geo); break;
      case 'logo': layoutLogo(ctx, el); break;
      case 'grid': layoutGrid(ctx, el); break;
      case 'mini': layoutMini(ctx, el); break;
      case 'legend': layoutLegend(ctx, el); break;
      case 'yeargrid': layoutYear(ctx, el); break;
      case 'key': layoutKey(ctx, el); break;
      case 'holidays': layoutHolidays(ctx, el); break;
    }
    checkClearance(ctx, el, geo);
  }
  return { key, trimW: geo.trimW, trimH: geo.trimH, bleed: geo.bleed, prims: ctx.prims, boxes: ctx.boxes, issues: ctx.issues, images: ctx.images };
}

function checkClearance(ctx: Ctx, el: Element, geo: ReturnType<typeof pageGeometry>) {
  if (el.type === 'image' || el.type === 'shape') return; // photos/panels may run to the edge
  const r = el.rect;
  const s = geo.safe;
  const inside = r.x >= s.x - 0.01 && r.y >= s.y - 0.01 && r.x + r.w <= s.x + s.w + 0.01 && r.y + r.h <= s.y + s.h + 0.01;
  if (!inside) ctx.issue('warning', 'outside-safe', `"${el.name}" extends outside the ${ctx.doc.product.safeMarginMm} mm safe margin and may be trimmed.`, el.id);
  const zone = geo.bindings.find((b) => r.x < b.x + b.w && r.x + r.w > b.x && r.y < b.y + b.h && r.y + r.h > b.y);
  if (zone)
    ctx.issue('warning', 'in-binding', ctx.key === 'carrier' ? `"${el.name}" overlaps the area where the tear-off pad is glued and will be covered.` : `"${el.name}" enters the ${ctx.doc.product.bindingClearanceMm} mm ${zone.label.toLowerCase()} zone.`, el.id);
}

function layoutTextEl(ctx: Ctx, el: TextEl) {
  const text = expandTokens(el.text, ctx.doc, ctx.key);
  let box = el.rect;
  if (!text.trim()) return; // an empty placeholder (e.g. no phone number) draws nothing, not even its panel
  if (el.panel) {
    ctx.prims.push({ k: 'rect', ...el.rect, r: el.panel.radiusMm, fill: el.panel.fill, opacity: el.panel.opacity, stroke: null, el: el.id });
    const p = el.panel.paddingMm;
    box = { x: el.rect.x + p, y: el.rect.y + p, w: Math.max(0.1, el.rect.w - 2 * p), h: Math.max(0.1, el.rect.h - 2 * p) };
  }
  const block = ctx.text(el.style, text, box, el.valign, el.id);
  if (block.overflow) ctx.issue('warning', 'text-overflow', `Text in "${el.name}" does not fit its box and will overflow.`, el.id);
}

/** Size an image needs to fill a frame (incl. bleed where it runs off the page) at the target resolution. */
export function sizeHint(r: Pick<Rect, 'w' | 'h'>, ppi: number): string {
  const px = (mm: number) => Math.ceil((mm / 25.4) * ppi);
  return `${+r.w.toFixed(1)} × ${+r.h.toFixed(1)} mm · ${px(r.w)} × ${px(r.h)} px at ${ppi} ppi`;
}

function bleedFrame(frame: Rect, geo: ReturnType<typeof pageGeometry>, radius: number): Rect {
  if (radius > 0 || geo.bleed <= 0) return frame;
  let { x, y, w, h } = frame;
  if (x <= EDGE_TOL) { w += x + geo.bleed; x = -geo.bleed; }
  if (y <= EDGE_TOL) { h += y + geo.bleed; y = -geo.bleed; }
  if (x + w >= geo.trimW - EDGE_TOL) w = geo.trimW + geo.bleed - x;
  if (y + h >= geo.trimH - EDGE_TOL) h = geo.trimH + geo.bleed - y;
  return { x, y, w, h };
}

/** Placed image rectangle for a frame (also used by the crop editor). */
export function placeImage(frame: Rect, meta: { width: number; height: number }, p: { fit: 'fill' | 'fit'; zoom: number; panX: number; panY: number }): Rect {
  const ar = meta.width / meta.height;
  let w: number, h: number;
  const cover = p.fit === 'fill';
  if ((frame.w / frame.h > ar) === cover) { w = frame.w; h = w / ar; } else { h = frame.h; w = h * ar; }
  w *= p.zoom; h *= p.zoom;
  return { x: frame.x + ((frame.w - w) / 2) * (1 + p.panX), y: frame.y + ((frame.h - h) / 2) * (1 + p.panY), w, h };
}

function layoutImage(ctx: Ctx, el: ImageEl, geo: ReturnType<typeof pageGeometry>) {
  const placement = ctx.doc.photos[photoPageKey(ctx.key, el)]?.[String(el.slot)];
  const label = `${el.name}`;
  if (!placement) {
    ctx.prims.push({ k: 'placeholder', kind: 'photo', ...el.rect, r: el.radiusMm, label, hint: sizeHint(bleedFrame(el.rect, geo, el.radiusMm), ctx.doc.checks.ppiTarget), el: el.id });
    if (el.required) ctx.issue('error', 'empty-frame', `"${el.name}" has no photograph.`, el.id);
    return;
  }
  const meta = ctx.env.assets[placement.assetId];
  if (!meta) {
    ctx.prims.push({ k: 'placeholder', kind: 'photo', ...el.rect, r: el.radiusMm, label: 'Missing image', el: el.id });
    ctx.issue('error', 'asset-missing', `The photograph in "${el.name}" is missing or could not be loaded (asset ${placement.assetId.slice(0, 8)}…). Replace it.`, el.id);
    return;
  }
  const frame = el.rect;
  const clip = placement.fit === 'fill' ? bleedFrame(frame, geo, el.radiusMm) : frame;
  const img = placeImage(clip, meta, placement);
  ctx.prims.push({ k: 'image', assetId: placement.assetId, clip, r: el.radiusMm, img, el: el.id });
  if (el.border && el.border.widthPt > 0) ctx.prims.push({ k: 'rect', ...frame, r: el.radiusMm, fill: null, opacity: 1, stroke: el.border, el: el.id });
  const ppi = Math.min(effectivePpi(meta.width, img.w), effectivePpi(meta.height, img.h));
  const bleedWanted = clip !== frame && (clip.x < frame.x || clip.y < frame.y || clip.w > frame.w || clip.h > frame.h);
  const tol = 0.01;
  const covers = img.x <= clip.x + tol && img.y <= clip.y + tol && img.x + img.w >= clip.x + clip.w - tol && img.y + img.h >= clip.y + clip.h - tol;
  const touchesEdge = frame.x <= EDGE_TOL || frame.y <= EDGE_TOL || frame.x + frame.w >= geo.trimW - EDGE_TOL || frame.y + frame.h >= geo.trimH - EDGE_TOL;
  if ((bleedWanted || touchesEdge) && !covers)
    ctx.issue('warning', 'no-bleed', `The photograph in "${el.name}" does not cover the ${geo.bleed} mm bleed — a white edge may show after trimming. Use "Fill" or zoom in.`, el.id);
  const { ppiTarget, ppiMinimum } = ctx.doc.checks;
  if (ppi < ppiMinimum)
    ctx.issue('error', 'low-ppi', `"${el.name}" prints at only ${Math.round(ppi)} ppi (minimum ${ppiMinimum}). Use a larger photograph or reduce zoom.`, el.id);
  else if (ppi < ppiTarget)
    ctx.issue('warning', 'low-ppi', `"${el.name}" prints at ${Math.round(ppi)} ppi; ${ppiTarget} ppi is recommended. It may look soft.`, el.id);
  // visible source region in pixels
  const ix0 = Math.max(clip.x, img.x), iy0 = Math.max(clip.y, img.y);
  const ix1 = Math.min(clip.x + clip.w, img.x + img.w), iy1 = Math.min(clip.y + clip.h, img.y + img.h);
  const sx = meta.width / img.w, sy = meta.height / img.h;
  ctx.images.push({
    elementId: el.id,
    assetId: placement.assetId,
    ppi,
    coversBleed: covers,
    source: { x: (ix0 - img.x) * sx, y: (iy0 - img.y) * sy, w: Math.max(0, ix1 - ix0) * sx, h: Math.max(0, iy1 - iy0) * sy },
  });
}

function layoutLogo(ctx: Ctx, el: LogoEl) {
  const id = ctx.doc.brand.logoAssetId;
  if (!id) {
    ctx.prims.push({ k: 'placeholder', kind: 'logo', ...el.rect, r: 0, label: 'Logo', hint: sizeHint(el.rect, ctx.doc.checks.ppiTarget), el: el.id });
    return;
  }
  const meta = ctx.env.assets[id];
  if (!meta) {
    ctx.prims.push({ k: 'placeholder', kind: 'logo', ...el.rect, r: 0, label: 'Missing logo', el: el.id });
    ctx.issue('error', 'asset-missing', 'The brand logo file is missing. Upload it again in Design → Branding.', el.id);
    return;
  }
  const ar = meta.width / meta.height;
  let w = el.rect.w, h = w / ar;
  if (h > el.rect.h) { h = el.rect.h; w = h * ar; }
  const x = el.align === 'left' ? el.rect.x : el.align === 'center' ? el.rect.x + (el.rect.w - w) / 2 : el.rect.x + el.rect.w - w;
  const y = el.rect.y + (el.rect.h - h) / 2;
  const r = { x, y, w, h };
  ctx.prims.push({ k: 'image', assetId: id, clip: r, r: 0, img: r, el: el.id });
  const ppi = effectivePpi(meta.width, w);
  if (ppi < ctx.doc.checks.ppiTarget)
    ctx.issue('warning', 'low-ppi', `The logo prints at ${Math.round(ppi)} ppi; ${ctx.doc.checks.ppiTarget} ppi is recommended. Use a larger logo file.`, el.id);
  ctx.images.push({ elementId: el.id, assetId: id, ppi, coversBleed: true, source: { x: 0, y: 0, w: meta.width, h: meta.height } });
}

function layoutGrid(ctx: Ctx, el: GridEl) {
  const { doc } = ctx;
  const info = pageInfo(doc, ctx.key);
  if (!info.month) {
    ctx.issue('info', 'grid-no-month', 'A calendar grid on a non-month page shows January.', el.id);
  }
  const month = info.month ?? 1;
  const g = el.style;
  const loc = getLocale(doc.calendar.locale);
  const mg = monthGrid(doc.year, month, doc.calendar.weekStart, doc.calendar.rows);
  const { x: gx, y: gy, w: gw, h: gh } = el.rect;
  const wnW = doc.calendar.weekNumbers ? g.weekNumbers.widthMm : 0;
  const headH = g.header.heightMm;
  const colW = (gw - wnW) / 7;
  const rows = mg.rows.length;
  const rowH = (gh - headH) / rows;
  const pad = g.cellPaddingMm;

  // header
  if (headH > 0) {
    if (g.header.fill) ctx.prims.push({ k: 'rect', x: gx, y: gy, w: gw, h: headH, r: 0, fill: g.header.fill, opacity: 1, stroke: null, el: el.id });
    const labels = g.header.labels === 'full' ? loc.weekdays : g.header.labels === 'narrow' ? loc.weekdaysNarrow : loc.weekdaysShort;
    const hf = ctx.font(g.header.text, el.id);
    if (hf) {
      const cap = capHeightMm(hf, g.header.text.sizePt);
      mg.columns.forEach((dow, c) => {
        const cx = gx + wnW + c * colW;
        const anchor = g.header.text.align;
        const ax = anchor === 'left' ? cx + pad : anchor === 'right' ? cx + colW - pad : cx + colW / 2;
        const w = ctx.line(g.header.text, labels[dow], ax, gy + headH / 2 + cap / 2, anchor, el.id);
        if (w > colW - 0.5) ctx.issue('warning', 'text-overflow', 'Weekday labels are wider than the columns. Use short/narrow labels or a smaller size.', el.id, 'header');
      });
      if (wnW) ctx.line(g.weekNumbers.text, loc.weekLabel, gx + wnW / 2, gy + headH / 2 + cap / 2, 'center', el.id);
    }
  }

  const dateFont = ctx.font(g.dateText, el.id);
  const dateCap = dateFont ? capHeightMm(dateFont, g.dateText.sizePt) : 0;
  if (rowH < dateCap + 2 * pad) ctx.issue('warning', 'text-overflow', 'Calendar rows are too short for the date numbers. Enlarge the grid or reduce the date size.', el.id, 'rows');

  mg.rows.forEach((row, r) => {
    const cy = gy + headH + r * rowH;
    if (wnW) {
      const wf = ctx.font(g.weekNumbers.text, el.id);
      if (wf) ctx.line(g.weekNumbers.text, String(mg.weekNumbers[r]), gx + wnW / 2, cy + pad + capHeightMm(wf, g.weekNumbers.text.sizePt), 'center', el.id);
    }
    row.forEach((cell, c) => {
      const cx = gx + wnW + c * colW;
      const occ = cell.inMonth ? ctx.occ.get(cell.key) ?? [] : [];
      const bg = cell.inMonth
        ? resolveCellBackground({ weekday: cell.weekday, occurrences: occ, override: doc.cellOverrides[cell.key]?.fill, saturday: g.saturday, sunday: g.sunday, weekdayFill: g.cellFill })
        : { tier: 'weekday' as const, fill: g.cellFill, textColor: null };
      if (bg.fill) ctx.prims.push({ k: 'rect', x: cx, y: cy, w: colW, h: rowH, r: 0, fill: bg.fill, opacity: 1, stroke: null, el: el.id });
      if (!cell.inMonth && doc.calendar.adjacent === 'blank') return;
      const style = cell.inMonth ? g.dateText : g.adjacentText;
      const color = cell.inMonth && bg.textColor ? bg.textColor : style.color;
      const f = cell.inMonth ? dateFont : ctx.font(style, el.id);
      if (!f) return;
      const cap = capHeightMm(f, style.sizePt);
      const label = String(cell.d);
      let bx: number, by: number, anchor: 'left' | 'center' | 'right';
      switch (g.datePosition) {
        case 'top-right': bx = cx + colW - pad; by = cy + pad + cap; anchor = 'right'; break;
        case 'top-center': bx = cx + colW / 2; by = cy + pad + cap; anchor = 'center'; break;
        case 'center': bx = cx + colW / 2; by = cy + rowH / 2 + cap / 2; anchor = 'center'; break;
        case 'bottom-right': bx = cx + colW - pad; by = cy + rowH - pad; anchor = 'right'; break;
        default: bx = cx + pad; by = cy + pad + cap; anchor = 'left';
      }
      ctx.line(style, label, bx, by, anchor, el.id, color);
      // event labels
      const labels = [...new Map(occ.filter((o) => o.display.showLabel).map((o) => [o.label, o])).values()];
      if (labels.length && cell.inMonth) {
        const ef = ctx.font(g.eventText, el.id);
        if (!ef) return;
        let top: number, bottom: number;
        if (g.datePosition === 'bottom-right') { top = cy + pad; bottom = by - cap - 0.8; }
        else if (g.datePosition === 'center') { top = by + 0.8; bottom = cy + rowH - pad; }
        else { top = by + 1; bottom = cy + rowH - pad; }
        let y = top;
        let clipped = false;
        for (const o of labels) {
          const box = { x: cx + pad, y, w: colW - 2 * pad, h: Math.max(0, bottom - y) };
          const lh = ptToMm(g.eventText.sizePt * g.eventText.lineHeight);
          if (box.h < lh - 0.01) { clipped = true; break; }
          const block = ctx.text(g.eventText, o.label, box, 'top', el.id, { maxLines: 3, color: o.display.textColor });
          if (block.overflow) clipped = true;
          y += block.height + 0.3;
        }
        if (clipped)
          ctx.issue('warning', 'text-clipped', `Event text on ${cell.d} ${loc.months[month - 1]} does not fit the day cell and is cut short.`, el.id, cell.key);
      }
    });
  });

  // lines
  if (g.innerLines && g.innerLines.widthPt > 0) {
    const s = g.innerLines;
    const bodyTop = gy + headH;
    for (let c = wnW ? 0 : 1; c < 7; c++) {
      const x = gx + wnW + c * colW;
      ctx.prims.push({ k: 'line', x1: x, y1: bodyTop, x2: x, y2: gy + gh, stroke: s, el: el.id });
    }
    const first = headH > 0 ? 0 : 1;
    const last = g.outerBorder ? rows - 1 : rows;
    for (let r = first; r <= last; r++) {
      if (r === 0 && !headH) continue;
      const y = bodyTop + r * rowH;
      ctx.prims.push({ k: 'line', x1: gx, y1: y, x2: gx + gw, y2: y, stroke: s, el: el.id });
    }
  }
  if (g.outerBorder && g.outerBorder.widthPt > 0)
    ctx.prims.push({ k: 'rect', x: gx, y: gy, w: gw, h: gh, r: 0, fill: null, opacity: 1, stroke: g.outerBorder, el: el.id });
}

function layoutMini(ctx: Ctx, el: MiniEl) {
  const info = pageInfo(ctx.doc, ctx.key);
  if (!info.month) return;
  const t = el.which === 'prev' ? prevMonth(ctx.doc.year, info.month) : nextMonth(ctx.doc.year, info.month);
  const loc = getLocale(ctx.doc.calendar.locale);
  const { x, y, w, h } = el.rect;
  const tf = ctx.font(el.title, el.id);
  const bf = ctx.font(el.text, el.id);
  if (!tf || !bf) return;
  const titleH = ptToMm(el.title.sizePt * 1.5);
  ctx.line(el.title, `${loc.months[t.m - 1]} ${t.y}`, x + w / 2, y + titleH * 0.75, 'center', el.id);
  const mg = monthGrid(t.y, t.m, ctx.doc.calendar.weekStart, 'fixed6');
  const cw = w / 7;
  const rh = (h - titleH) / 7;
  const cap = capHeightMm(bf, el.text.sizePt);
  mg.columns.forEach((dow, c) => ctx.line({ ...el.text, color: el.title.color }, loc.weekdaysNarrow[dow], x + c * cw + cw / 2, y + titleH + rh / 2 + cap / 2, 'center', el.id));
  mg.rows.forEach((row, r) => row.forEach((cell, c) => {
    if (!cell.inMonth) return;
    ctx.line(el.text, String(cell.d), x + c * cw + cw / 2, y + titleH + (r + 1) * rh + rh / 2 + cap / 2, 'center', el.id);
  }));
  if (rh < cap * 1.2) ctx.issue('warning', 'text-overflow', `"${el.name}" is too small for its text.`, el.id);
}

function layoutLegend(ctx: Ctx, el: LegendEl) {
  const info = pageInfo(ctx.doc, ctx.key);
  if (!info.month) return;
  const loc = getLocale(ctx.doc.calendar.locale);
  const items: string[] = [];
  for (let d = 1; d <= daysInMonth(ctx.doc.year, info.month); d++) {
    const k = dateKey(ctx.doc.year, info.month, d);
    const seen = new Set<string>();
    for (const o of ctx.occ.get(k) ?? []) {
      if (!o.display.legend || seen.has(o.label)) continue;
      // ranges: only list on their first day within the month
      const prev = d > 1 ? ctx.occ.get(dateKey(ctx.doc.year, info.month, d - 1)) ?? [] : [];
      if (prev.some((p) => p.eventId === o.eventId && p.setId === o.setId)) continue;
      seen.add(o.label);
      items.push(`${d} ${loc.monthsShort[info.month - 1]} – ${o.label}`);
    }
  }
  void weekday;
  if (!items.length) return;
  const block = ctx.text(el.style, items.join('   ·   '), el.rect, 'top', el.id);
  if (block.overflow) ctx.issue('warning', 'text-overflow', 'The legend has more events than fit in its box.', el.id);
}

/** Year at a glance: 12 mini months (6 on a tent face) with holiday shading and weekend colours. */
function layoutYear(ctx: Ctx, el: YearGridEl) {
  const { doc } = ctx;
  const st = el.style;
  const loc = getLocale(doc.calendar.locale);
  const cols = el.columns;
  const months = pageMonths(ctx.key);
  const rows = Math.ceil(months.length / cols);
  const bw = (el.rect.w - (cols - 1) * el.gapXmm) / cols;
  const bh = (el.rect.h - (rows - 1) * el.gapYmm) / rows;
  const titleH = Math.min(st.titleHeightMm, bh * 0.3);
  const cellW = bw / 7;
  const rowH = (bh - titleH) / 7; // weekday row + 6 week rows
  const tf = ctx.font(st.title, el.id), wf = ctx.font(st.weekday, el.id), df = ctx.font(st.date, el.id);
  if (!tf || !wf || !df) return;
  const tCap = capHeightMm(tf, st.title.sizePt), wCap = capHeightMm(wf, st.weekday.sizePt), dCap = capHeightMm(df, st.date.sizePt);
  if (dCap > rowH * 0.8 || measureDigits(df, st) > cellW * 0.98)
    ctx.issue('warning', 'text-overflow', `Dates in "${el.name}" are too large for the day cells. Reduce the date size or enlarge the calendar.`, el.id, 'dates');
  const labels = st.weekdayLabels === 'short' ? loc.weekdaysShort : loc.weekdaysNarrow;
  months.forEach((m, i) => {
    const c = i % cols, r = Math.floor(i / cols);
    const bx = el.rect.x + c * (bw + el.gapXmm), by = el.rect.y + r * (bh + el.gapYmm);
    if (titleH > 0) {
      if (st.titleFill) ctx.prims.push({ k: 'rect', x: bx, y: by, w: bw, h: titleH * 0.86, r: Math.min(st.titleRadiusMm, titleH * 0.43), fill: st.titleFill, opacity: 1, stroke: null, el: el.id });
      const tw = ctx.line(st.title, loc.months[m - 1], bx + bw / 2, by + titleH * 0.43 + tCap / 2, 'center', el.id);
      if (tw > bw) ctx.issue('warning', 'text-overflow', `Month names in "${el.name}" are wider than the month blocks.`, el.id, 'titles');
    }
    const mg = monthGrid(doc.year, m, doc.calendar.weekStart, 'fixed6');
    const hy = by + titleH;
    mg.columns.forEach((dow, i) => {
      const color = dow === 6 && st.saturdayColor ? st.saturdayColor : dow === 0 && st.sundayColor ? st.sundayColor : st.weekday.color;
      ctx.line(st.weekday, labels[dow], bx + i * cellW + cellW / 2, hy + rowH / 2 + wCap / 2, 'center', el.id, color);
    });
    mg.rows.forEach((row, ri) => row.forEach((cell, ci) => {
      if (!cell.inMonth) return;
      const cx = bx + ci * cellW, cy = hy + (ri + 1) * rowH;
      const occ = ctx.occ.get(cell.key) ?? [];
      const bg = resolveCellBackground({
        weekday: cell.weekday, occurrences: occ, override: doc.cellOverrides[cell.key]?.fill,
        saturday: { fill: st.weekendFill, textColor: st.saturdayColor }, sunday: { fill: st.weekendFill, textColor: st.sundayColor }, weekdayFill: null,
      });
      const inset = Math.min(cellW, rowH) * 0.06;
      if (bg.fill) ctx.prims.push({ k: 'rect', x: cx + inset, y: cy + inset, w: cellW - 2 * inset, h: rowH - 2 * inset, r: st.shadeRadiusMm, fill: bg.fill, opacity: 1, stroke: null, el: el.id });
      ctx.line(st.date, String(cell.d), cx + cellW / 2, cy + rowH / 2 + dCap / 2, 'center', el.id, bg.textColor ?? st.date.color);
    }));
  });
}

const measureDigits = (f: NonNullable<ReturnType<Ctx['font']>>, st: { date: TextStyle }) => shapeRun(f, '28', st.date.sizePt, st.date.letterSpacingPt).width;

const KIND_KEY_LABEL: Record<string, string> = { public: 'Public holidays', school: 'School holidays', personal: 'Birthdays & anniversaries', business: 'Business dates', custom: '' };

/** Colour key for visible, shaded event sets. */
function layoutKey(ctx: Ctx, el: KeyEl) {
  const items = ctx.doc.eventSets.filter((s) => s.display.visible && s.display.shade).map((s) => ({ label: KIND_KEY_LABEL[s.kind] || s.name, fill: s.display.background }));
  const seen = new Set<string>();
  const unique = items.filter((i) => !seen.has(i.label) && seen.add(i.label));
  if (!unique.length) return;
  const f = ctx.font(el.style, el.id);
  if (!f) return;
  const cap = capHeightMm(f, el.style.sizePt);
  const sw = cap * 1.6, gap = cap * 0.8, itemGap = cap * 2.5;
  const widths = unique.map((i) => sw + gap + shapeRun(f, el.style.uppercase ? i.label.toUpperCase() : i.label, el.style.sizePt, el.style.letterSpacingPt).width);
  const total = widths.reduce((a, b) => a + b, 0) + itemGap * (unique.length - 1);
  if (total > el.rect.w + 0.1) ctx.issue('warning', 'text-overflow', 'The holiday key is wider than its box.', el.id);
  let x = el.align === 'left' ? el.rect.x : el.align === 'right' ? el.rect.x + el.rect.w - total : el.rect.x + (el.rect.w - total) / 2;
  const mid = el.rect.y + el.rect.h / 2;
  unique.forEach((it, i) => {
    ctx.prims.push({ k: 'rect', x, y: mid - sw / 2, w: sw, h: sw, r: sw * 0.2, fill: it.fill, opacity: 1, stroke: { widthPt: 0.25, color: { space: 'cmyk', c: 0, m: 0, y: 0, k: 40 } }, el: el.id });
    ctx.line(el.style, it.label, x + sw + gap, mid + cap / 2, 'left', el.id);
    x += widths[i] + itemGap;
  });
}

// ---------------------------------------------------------------- public holiday list

/** Shorter names for the holiday list ("National Women's Day" → "Women's Day"). */
const SHORT_HOLIDAY_NAMES: Record<string, string> = {
  "National Women's Day": "Women's Day",
  'Day of Reconciliation': 'Reconciliation Day',
};
export const shortHolidayName = (label: string) => SHORT_HOLIDAY_NAMES[label] ?? label.replace(/^National /, '');

export interface HolidayListItem { date: string; text: string; marked: boolean }

/**
 * The entries of a holiday & event list from the visible event sets of the
 * chosen kinds (public, school, birthdays …), within the calendar year (or
 * the months on this page): "01/01 - New Year's Day", "01 Jan - New Year's
 * Day", and for periods "20/03–05/04 - School holidays". With observed
 * "marker", a Sunday public holiday is listed once, on its observed Monday,
 * with the marker ("22/03 - Human Rights Day*").
 */
export function holidayListItems(
  doc: ProjectDoc,
  el: Pick<HolidayListEl, 'dateFormat' | 'names' | 'range' | 'observed' | 'marker'> & { kinds?: HolidayListEl['kinds'] },
  key: PageKey,
  occ: Map<string, Occurrence[]>,
): HolidayListItem[] {
  const loc = getLocale(doc.calendar.locale);
  const info = pageInfo(doc, key);
  const months = new Set(el.range === 'page' ? (info.month ? [info.month] : pageMonths(key)) : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
  const kinds = el.kinds ?? ['public'];
  const sets = doc.eventSets.filter((s) => kinds.includes(s.kind) && s.display.visible);
  const items: HolidayListItem[] = [];
  const name = (label: string, kind: string) => (el.names === 'short' && kind === 'public' ? shortHolidayName(label) : label);
  const fmt = (date: string) => {
    const [, m, d] = date.split('-');
    return el.dateFormat === 'numeric' ? `${d}/${m}` : `${d} ${loc.monthsShort[+m - 1]}`;
  };
  const inScope = (date: string) => date.startsWith(`${doc.year}-`) && months.has(+date.slice(5, 7));
  const dates = [...occ.keys()].sort();
  for (const set of sets) {
    const byId = new Map(set.events.map((e) => [e.id, e]));
    // originals that have an observed day (they fall on a Sunday)
    const observedOf = new Set(set.events.filter((e) => e.observed && e.originalDate).map((e) => e.originalDate!));
    // consecutive days of one event make one entry (school holidays, ranges)
    const runs = new Map<string, { start: string; end: string; o: Occurrence }[]>();
    for (const date of dates)
      for (const o of occ.get(date)!) {
        if (o.setId !== set.id) continue;
        const list = runs.get(o.eventId) ?? [];
        const last = list[list.length - 1];
        if (last && addDays(last.end, 1) === date) last.end = date;
        else list.push({ start: date, end: date, o });
        runs.set(o.eventId, list);
      }
    for (const list of runs.values())
      for (const r of list) {
        // keep the entry when any of its days falls in scope
        let any = false;
        for (let d = r.start; d <= r.end; d = addDays(d, 1)) if (inScope(d)) { any = true; break; }
        if (!any) continue;
        const when = r.start === r.end ? fmt(r.start) : `${fmt(r.start)}–${fmt(r.end)}`;
        const o = r.o;
        if (o.observed) {
          const orig = byId.get(o.eventId)?.originalDate;
          const origLabel = (orig && occ.get(orig)?.find((x) => x.setId === set.id && !x.observed)?.label) || o.label.replace(/^Public holiday \((.*) observed\)$/, '$1');
          items.push({ date: r.start, text: `${when} - ${name(origLabel, set.kind)}${el.marker}`, marked: !!el.marker });
        } else {
          if (el.observed === 'marker' && r.start === r.end && observedOf.has(r.start)) continue; // listed on its observed Monday instead
          items.push({ date: r.start, text: `${when} - ${name(o.label, set.kind)}`, marked: false });
        }
      }
  }
  const seen = new Set<string>();
  return items.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)).filter((i) => !seen.has(i.text) && seen.add(i.text));
}

function layoutHolidays(ctx: Ctx, el: HolidayListEl) {
  const items = holidayListItems(ctx.doc, el, ctx.key, ctx.occ);
  if (!items.length) {
    if (!ctx.doc.eventSets.some((s) => el.kinds.includes(s.kind) && s.display.visible))
      ctx.issue('warning', 'no-holidays', `"${el.name}" is empty: no visible event set of the chosen kinds (Content → Holidays & events).`, el.id);
    return;
  }
  const f = ctx.font(el.style, el.id);
  if (!f) return;
  const lineH = ptToMm(el.style.sizePt * el.style.lineHeight);
  const showNote = !!el.footnote.trim() && items.some((i) => i.marked);
  const noteStyle = { ...el.style, sizePt: +(el.style.sizePt * 0.85).toFixed(2), align: el.style.align };
  const noteH = showNote ? ptToMm(noteStyle.sizePt * noteStyle.lineHeight) : 0;
  const box = { ...el.rect, h: Math.max(0.1, el.rect.h - noteH) };
  let overflow = false;
  const width = (t: string) => shapeRun(f, el.style.uppercase ? t.toUpperCase() : t, el.style.sizePt, el.style.letterSpacingPt).width;
  if (el.layout === 'row') {
    // pack whole entries into lines, never splitting an entry
    const sep = el.separator;
    const lines: string[] = [];
    let cur = '';
    for (const it of items) {
      const next = cur ? `${cur}${sep}${it.text}` : it.text;
      if (cur && width(next) > box.w) { lines.push(cur); cur = it.text; } else cur = next;
    }
    if (cur) lines.push(cur);
    overflow = ctx.text(el.style, lines.join('\n'), box, 'top', el.id, { wrap: false }).overflow;
  } else {
    const cols = Math.min(el.columns, items.length);
    const per = Math.ceil(items.length / cols);
    const gap = Math.min(4, box.w * 0.04);
    const cw = (box.w - gap * (cols - 1)) / cols;
    for (let c = 0; c < cols; c++) {
      const part = items.slice(c * per, (c + 1) * per).map((i) => i.text);
      const b = ctx.text(el.style, part.join('\n'), { x: box.x + c * (cw + gap), y: box.y, w: cw, h: box.h }, 'top', el.id, { wrap: false });
      overflow ||= b.overflow;
    }
    void lineH;
  }
  if (showNote) ctx.text(noteStyle, el.footnote, { x: el.rect.x, y: el.rect.y + el.rect.h - noteH, w: el.rect.w, h: noteH }, 'top', el.id, { wrap: false });
  if (overflow) ctx.issue('warning', 'text-overflow', `The holiday list "${el.name}" does not fit its box. Make the block bigger, use more columns or a smaller type size.`, el.id);
}
